// src/utils/portfolioUtils.ts

import { PortfolioMatcher } from "@/types/Matchers";
import { Portfolio } from "@/types/Portfolio";

export type HighlightInfo = {
  portfolioId: number;
  portfolioName: string; // Added portfolioName
  color: string;
  matchedTickers: string[]; // Contains both matched tickers and keywords
};

export function getHighestPriorityMatchingPortfolio<T>(
  item: T,
  portfolios: Portfolio[],
  matcher: (item: T, portfolios: Portfolio[]) => boolean
): Portfolio | undefined {
  // Sort portfolios by priority
  const sortedPortfolios = portfolios.sort((a, b) => a.priority - b.priority);

  return sortedPortfolios.find((portfolio) => matcher(item, [portfolio]));
}

export const mapDataWithMatchingPortfolios = <T>(
  data: T[] | undefined,
  portfoliosToFilter: Portfolio[],
  portfoliosToHighlight: Portfolio[],
  portfolioMatcher: PortfolioMatcher<T>
): [T, Portfolio | undefined][] => {
  if (!data) {
    return [];
  }

  let filteredData: [T, Portfolio | undefined][] =
    data?.map((item) => [item, undefined]) ?? [];

  // Filter out items that do not match any of the portfolios in portfoliosToFilter
  if (portfoliosToFilter.length > 0 && data) {
    filteredData = filteredData
      .map(([item]) => {
        const matchingPortfolio = getHighestPriorityMatchingPortfolio<T>(
          item,
          portfoliosToFilter,
          portfolioMatcher
        );

        return [item, matchingPortfolio];
      })
      .filter(([, portfolio]) => portfolio !== undefined) as [T, Portfolio][];

    return filteredData;
  }

  if (portfoliosToHighlight.length > 0 && data) {
    filteredData = filteredData.map(([item]) => {
      const matchingPortfolio = getHighestPriorityMatchingPortfolio(
        item,
        portfoliosToHighlight,
        portfolioMatcher
      );
      return [item, matchingPortfolio];
    });
  }

  return filteredData;
};

export const buildPortfolioSearchQuery = (
  allPortfolios: Portfolio[],
  selectedPortfolioIds: number[],
  searchTerm: string
) => {
  const selectedPortfolios = allPortfolios.filter((portfolio) =>
    selectedPortfolioIds.includes(portfolio.id)
  );

  const tickers = selectedPortfolios.flatMap((p) => p.tickers);

  const uniqueTickers = Array.from(new Set(tickers));

  if (uniqueTickers.length === 0) {
    return searchTerm;
  }

  return `${searchTerm} (${uniqueTickers.join(" OR ")})`;
};
